/**
 * ─────────────────────────────────────────────
 * src/store/slices/authSlice.js
 * ─────────────────────────────────────────────
 *
 * WHY THIS FILE EXISTS:
 * This is the HEART of authentication in Redux.
 * It contains 3 things in one file (that's what a "slice" is):
 *
 *   1. STATE      — what auth data we store globally
 *   2. REDUCERS   — functions that UPDATE that state
 *   3. THUNKS     — async functions that call the API,
 *                   then trigger the reducers
 *
 * ─────────────────────────────────────────────
 * UNDERSTANDING createSlice:
 * ─────────────────────────────────────────────
 * createSlice() from @reduxjs/toolkit combines:
 *   - initialState (what state looks like at start)
 *   - reducers (synchronous state changes)
 *   - extraReducers (handles async thunk results)
 *
 * ─────────────────────────────────────────────
 * UNDERSTANDING createAsyncThunk:
 * ─────────────────────────────────────────────
 * createAsyncThunk is a factory that creates an async action.
 * When you call dispatch(loginUser(credentials)), it:
 *
 *   1. Automatically dispatches: { type: 'auth/loginUser/pending' }
 *      → Your reducer sets isLoading: true
 *
 *   2. Runs your async function (calls authService.login)
 *
 *   3a. If it succeeds → dispatches: { type: 'auth/loginUser/fulfilled', payload: data }
 *       → Your reducer sets user, token, isLoading: false
 *
 *   3b. If it fails → dispatches: { type: 'auth/loginUser/rejected', payload: errorMessage }
 *       → Your reducer sets error, isLoading: false
 *
 * You get all 3 states (pending/fulfilled/rejected) for FREE.
 * This replaces your manual setIsLoading(true/false) and try/catch in components.
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../services/authService";

// ─────────────────────────────────────────────
// HELPER: Extract error message from axios error
//
// WHY: Axios errors are nested objects.
//      error.response.data.message is your backend's error message.
//      error.message is a generic JS error message.
//      This helper safely extracts whichever one exists.
// ─────────────────────────────────────────────
const extractError = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

// ─────────────────────────────────────────────
// INITIAL STATE
// This is what the auth state looks like when the app first loads.
//
// We initialize user and token FROM localStorage so that if the
// user refreshes the page, they stay logged in.
// (Redux state resets on refresh — localStorage persists)
// ─────────────────────────────────────────────
const storedToken = localStorage.getItem("token");
const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
})();

const initialState = {
  // ── Who is logged in ──────────────────────
  user: storedUser, // { _id, name, email, role } or null
  token: storedToken, // JWT string or null

  // ── Reset password flow state ─────────────
  // These track the forgot-password flow to prevent direct URL access
  resetEmail: null, // Email entered in forgot-password form
  resetOtpToken: null, // Token received after successful OTP verification
  resetOtpVerified: false, // Did user successfully verify OTP?

  // ── Loading states per feature ────────────
  // WHY separate loading states?
  // If you use one isLoading for everything, when login is loading,
  // it might accidentally show "loading" state on the OTP page too.
  // Separate flags = precise UI control.
  isLoginLoading: false,
  isForgotLoading: false,
  isOtpLoading: false,
  isResetLoading: false,
  isSetPasswordLoading: false,

  // ── Error per feature ─────────────────────
  loginError: null,
  forgotError: null,
  otpError: null,
  resetError: null,
  setPasswordError: null,

  // ── Success flags ─────────────────────────
  // Used to trigger navigation after success.
  // Components watch these and navigate when they become true.
  forgotPasswordSuccess: false, // → navigate to /otp
  otpVerified: false, // → navigate to /reset-password
  resetPasswordSuccess: false, // → navigate to /
  setPasswordSuccess: false, // → navigate to /
};

// ═════════════════════════════════════════════
// ASYNC THUNKS
// These are your API call actions.
// Each one: calls the service → handles success/error automatically.
// ═════════════════════════════════════════════

// ─────────────────────────────────────────────
// 1. LOGIN
// Thunk name format: 'sliceName/actionName'
// Second argument is the async function.
//   - First param: the data you pass when dispatching
//   - Second param: { rejectWithValue } — used to pass custom error to rejected state
// ─────────────────────────────────────────────
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      // authService.login calls axios.post('/auth/login', credentials)
      // Returns: { success, message, data: { token, user } }
      const data = await authService.login(credentials);

      // Save to localStorage so user stays logged in after refresh
      localStorage.setItem("token", data.data.token);
      localStorage.setItem("user", JSON.stringify(data.data.user));

      // Whatever we return here becomes `action.payload` in the fulfilled reducer
      return data.data; // { token, user }
    } catch (error) {
      // rejectWithValue sends this to the rejected reducer as action.payload
      // Without it, you'd only get a generic error object
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// 2. FORGOT PASSWORD
// ─────────────────────────────────────────────
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const data = await authService.forgotPassword({ email });
      // Store email for the OTP verification flow
      sessionStorage.setItem("resetEmail", email);
      return { ...data, email }; // Return email to store in state
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// 3. VERIFY OTP
// ─────────────────────────────────────────────
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const data = await authService.verifyOtp({ email, otp });
      // Store OTP verification token/flag for reset password flow
      sessionStorage.setItem("resetOtpVerified", "true");
      return { ...data, email }; // Return email to maintain state
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// 4. RESET PASSWORD
// ─────────────────────────────────────────────
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const data = await authService.resetPassword({ email, password });
      return data; // { success: true, message: "Password reset successfully." }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// 5. SET PASSWORD (from invite link)
// ─────────────────────────────────────────────
export const setPassword = createAsyncThunk(
  "auth/setPassword",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const data = await authService.setPassword({ token, password });
      return data; // { success: true, message: "Password set successfully." }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ═════════════════════════════════════════════
// THE SLICE
// createSlice creates:
//   - actions (named exports from slice.actions)
//   - reducer (default export from slice.reducer)
// ═════════════════════════════════════════════
const authSlice = createSlice({
  name: "auth", // Prefix for all action types: "auth/..."
  initialState,

  // ─────────────────────────────────────────
  // SYNCHRONOUS REDUCERS
  // These handle simple, instant state changes.
  // No API calls here — just state updates.
  //
  // Each reducer is a function that receives:
  //   state  — current state (can mutate directly thanks to Immer library built into RTK)
  //   action — { type, payload }
  // ─────────────────────────────────────────
  reducers: {
    // Called when user clicks Logout button
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.loginError = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },

    // Clear errors (useful when user starts typing again after an error)
    clearAuthErrors: (state) => {
      state.loginError = null;
      state.forgotError = null;
      state.otpError = null;
      state.resetError = null;
      state.setPasswordError = null;
    },

    // Reset success flags (called after navigation happens)
    // Without this, if user goes back to forgot-password page,
    // it might immediately redirect them because forgotPasswordSuccess is still true
    clearAuthSuccess: (state) => {
      state.forgotPasswordSuccess = false;
      state.otpVerified = false;
      state.resetPasswordSuccess = false;
      state.setPasswordSuccess = false;
    },

    // Clear reset session data (called after successful password reset/login)
    // Prevents users from accessing OTP/reset pages after flow is complete
    clearResetSession: (state) => {
      state.resetEmail = null;
      state.resetOtpToken = null;
      state.resetOtpVerified = false;
      sessionStorage.removeItem("resetEmail");
      sessionStorage.removeItem("resetOtpVerified");
    },
  },

  // ─────────────────────────────────────────
  // EXTRA REDUCERS
  // These handle the 3 states of each async thunk:
  //   pending   → request is in flight
  //   fulfilled → request succeeded
  //   rejected  → request failed
  //
  // builder.addCase(thunk.pending, handler)
  // builder.addCase(thunk.fulfilled, handler)
  // builder.addCase(thunk.rejected, handler)
  // ─────────────────────────────────────────
  extraReducers: (builder) => {
    // ─────────────────────────────────────────
    // LOGIN
    // ─────────────────────────────────────────
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoginLoading = true;
        state.loginError = null; // Clear previous error when retrying
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        // action.payload = { token, user } (what we returned from the thunk)
        state.isLoginLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.loginError = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        // action.payload = the string we passed to rejectWithValue()
        state.isLoginLoading = false;
        state.loginError = action.payload;
        // Clear any stale user/token on login failure
        state.user = null;
        state.token = null;
      });

    // ─────────────────────────────────────────
    // FORGOT PASSWORD
    // ─────────────────────────────────────────
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.isForgotLoading = true;
        state.forgotError = null;
        state.forgotPasswordSuccess = false;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isForgotLoading = false;
        state.forgotPasswordSuccess = true; // Component watches this to navigate
        state.resetEmail = action.payload.email; // Store email for OTP verification flow
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isForgotLoading = false;
        state.forgotError = action.payload;
        state.forgotPasswordSuccess = false;
      });

    // ─────────────────────────────────────────
    // VERIFY OTP
    // ─────────────────────────────────────────
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.isOtpLoading = true;
        state.otpError = null;
        state.otpVerified = false;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isOtpLoading = false;
        state.otpVerified = true; // Component watches this to navigate
        state.resetOtpVerified = true; // Mark OTP as verified for reset flow
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isOtpLoading = false;
        state.otpError = action.payload;
        state.otpVerified = false;
      });

    // ─────────────────────────────────────────
    // RESET PASSWORD
    // ─────────────────────────────────────────
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isResetLoading = true;
        state.resetError = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isResetLoading = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isResetLoading = false;
        state.resetError = action.payload;
      });

    // ─────────────────────────────────────────
    // SET PASSWORD (invite link)
    // ─────────────────────────────────────────
    builder
      .addCase(setPassword.pending, (state) => {
        state.isSetPasswordLoading = true;
        state.setPasswordError = null;
        state.setPasswordSuccess = false;
      })
      .addCase(setPassword.fulfilled, (state) => {
        state.isSetPasswordLoading = false;
        state.setPasswordSuccess = true;
      })
      .addCase(setPassword.rejected, (state, action) => {
        state.isSetPasswordLoading = false;
        state.setPasswordError = action.payload;
      });
  },
});

// ─────────────────────────────────────────────
// Export synchronous action creators
// These are used in components like:
//   dispatch(logout())
//   dispatch(clearAuthErrors())
// ─────────────────────────────────────────────
export const { logout, clearAuthErrors, clearAuthSuccess, clearResetSession } =
  authSlice.actions;

// ─────────────────────────────────────────────
// Export the reducer as default
// This is what goes into store/index.js:
//   auth: authReducer
// ─────────────────────────────────────────────
export default authSlice.reducer;
